import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { INDUSTRIES, INDUSTRY_DESCRIPTIONS, INDUSTRY_LABELS, type Industry, type OperatorIndustryLayouts } from '@kontor/shared';
import { ApiError } from '../api';
import { LayoutDialog } from '../components/LayoutDialog';
import { operatorApi } from './operatorApi';

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Branchen: Der Betreiber legt je Branche das Menü-Layout fest, also welche Bereiche ausgeblendet sind (Änderungswunsch vom 19.10.2026). Ein Unternehmen, das seine Branche wählt, bekommt eine
 * Kopie und kann sie für sich anpassen. Spätere Änderungen hier berühren Unternehmen mit eigener Anpassung nicht. Die anderen übernehmen die Änderung, wenn sie ihr Layout zurücksetzen oder die
 * Einrichtung erneut anwenden.
 */
export function OperatorIndustriesPage() {
  const client = useQueryClient();
  const [editing, setEditing] = useState<Industry | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = useQuery({ queryKey: ['operator-industries'], queryFn: async () => (await operatorApi<{ data: OperatorIndustryLayouts }>('GET', '/industries')).data });

  const save = useMutation({
    mutationFn: ({ industry, hidden, groupLabels }: { industry: Industry; hidden: string[]; groupLabels: Record<string, string> }) => operatorApi<{ data: OperatorIndustryLayouts }>('PUT', `/industries/${industry}`, { hidden, groupLabels }),
    onSuccess: (res, vars) => {
      client.setQueryData(['operator-industries'], res.data);
      setEditing(null);
      setMessage({ ok: true, text: `Das Layout für ${INDUSTRY_LABELS[vars.industry]} ist gespeichert. Neue Wahlen der Branche übernehmen es sofort.` });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das Speichern hat nicht geklappt.'),
  });
  const clear = useMutation({
    mutationFn: (industry: Industry) => operatorApi<{ data: OperatorIndustryLayouts }>('DELETE', `/industries/${industry}`),
    onSuccess: (res, industry) => {
      client.setQueryData(['operator-industries'], res.data);
      setEditing(null);
      setMessage({ ok: true, text: `Die Festlegung für ${INDUSTRY_LABELS[industry]} ist entfernt. Die Branche blendet nichts mehr aus.` });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das Entfernen hat nicht geklappt.'),
  });

  const layouts = data.data?.layouts;
  return (
    <>
      <p><Link to="/betreiber" className="back-link">← Alle Mandanten</Link></p>
      <div className="page-head"><h1>Branchen und Menü-Layouts</h1></div>
      <p className="muted">
        Legen Sie je Branche fest, welche Bereiche im Menü ausgeblendet sind und wie die Hauptbereiche heißen. Ein Unternehmen bekommt mit der Festlegung seiner Branche (Mandantenseite → Branche) eine Kopie und kann sie in Einrichtung → Firmendaten für sich anpassen oder auf Ihre Vorgabe zurücksetzen.
        Änderungen hier berühren Unternehmen mit eigener Anpassung nicht. Ausgeblendet heißt nur: nicht im Menü. Die Module bleiben lizenziert, die Seiten über ihre Adresse erreichbar.
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {data.isLoading && <p className="muted">Wird geladen …</p>}
      {data.isError && <div className="alert alert--error" role="alert">Die Layouts konnten nicht geladen werden.</div>}

      {layouts && (
        <div className="grid-cards">
          {INDUSTRIES.map((i) => {
            const layout = layouts[i];
            const hidden = layout?.hidden ?? null;
            const renamed = Object.keys(layout?.groupLabels ?? {}).length;
            return (
              <article key={i} className="card">
                <h2>{INDUSTRY_LABELS[i]}</h2>
                <p className="muted small">{INDUSTRY_DESCRIPTIONS[i]}</p>
                <p>{hidden === null ? 'Noch nichts festgelegt: Die Branche blendet nichts aus.' : hidden.length === 0 ? 'Festgelegt: Alle Bereiche sind sichtbar.' : `Festgelegt: ${hidden.length} ${hidden.length === 1 ? 'Bereich' : 'Bereiche'} ausgeblendet.`}{renamed > 0 && ` ${renamed} ${renamed === 1 ? 'Hauptbereich' : 'Hauptbereiche'} umbenannt.`}</p>
                <div className="form-actions">
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => { setError(null); setEditing(i); }}>Layout bearbeiten</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {data.data?.updatedAt && <p className="muted small">Zuletzt geändert am {dateTime.format(new Date(data.data.updatedAt))}{data.data.updatedBy ? ` von ${data.data.updatedBy}` : ''}.</p>}

      {editing && layouts && (
        <LayoutDialog
          key={editing}
          title={`Layout für ${INDUSTRY_LABELS[editing]}`}
          intro="Ein Haken heißt: Der Bereich steht im Menü. Entfernen Sie den Haken bei Bereichen, die in dieser Branche selten gebraucht werden. Unter „Name im Menü“ benennen Sie einen Hauptbereich für diese Branche um, zum Beispiel „Betrieb“ als „Fuhrpark“. Die Einrichtung bleibt immer sichtbar, und die Unternehmen können die Namen nicht ändern."
          hidden={layouts[editing]?.hidden ?? []}
          groupLabels={layouts[editing]?.groupLabels ?? {}}
          renamable
          saving={save.isPending || clear.isPending}
          error={error}
          onSave={(hidden, groupLabels) => { setError(null); save.mutate({ industry: editing, hidden, groupLabels }); }}
          onReset={layouts[editing] !== null ? () => { setError(null); clear.mutate(editing); } : undefined}
          resetLabel="Festlegung entfernen"
          resetHint="Ohne Festlegung blendet die Branche nichts aus. Unternehmen mit eigener Anpassung behalten ihre Auswahl."
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
